import React, { useState, useRef } from "react";
import Cropper from "react-cropper";
import "cropperjs/dist/cropper.css";
import { Modal } from "react-bootstrap";

// Define the props interface
interface ImageCropperProps {
  show: boolean;
  onHide: () => void;
  imageUrl: string;
  aspectRatio: number;
  onCropComplete: (croppedImageUrl: string, blob: Blob) => void;
  width?: number; // Optional with default value
  height?: number; // Optional with default value
}

/**
 * A component for cropping images with a fixed aspect ratio and dimensions.
 *
 * @remarks
 * This component uses the react-cropper library to provide image cropping functionality.
 * It allows users to upload an image, crop a specific area with predefined width and height,
 * and get the cropped image data.
 *
 * show - Boolean to control the visibility of the modal.
 * onHide - Callback function to hide the modal.
 * imageUrl - The URL of the image to be cropped.
 * aspectRatio - The aspect ratio of the crop area.
 * onCropComplete - Callback function with the cropped image URL and blob.
 * width - Fixed width for the crop area (default to 200).
 * height - Fixed height for the crop area (default to 100).
 */
const ImageCropper: React.FC<ImageCropperProps> = ({
  show,
  onHide,
  imageUrl,
  aspectRatio,
  onCropComplete,
  width = 546, // Default width as per requirement
  height = 300, // Default height as per requirement
}) => {
  const [cropper, setCropper] = useState<any>(null);
  const cropperRef = useRef<HTMLImageElement>(null);

  const handleCrop = (): void => {
    if (cropper) {
      const canvas: HTMLCanvasElement | null = cropper.getCroppedCanvas({
        width: width,
        height: height,
      });

      if (canvas) {
        canvas.toBlob(
          (blob: Blob | null) => {
            if (blob) {
              const croppedImageUrl: string = URL.createObjectURL(blob);
              onCropComplete(croppedImageUrl, blob);
              onHide();
            }
          },
          "image/jpeg",
          0.9
        );
      }
    }
  };

  const handleCropperReady = (instance: any): void => {
    setCropper(instance);
    // Set fixed crop box size after initialization
    setTimeout(() => {
      instance.setCropBoxData({
        width: width,
        height: height,
      });
    }, 100);
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title className="text-white fw-bold">Crop Image</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div style={{ height: "400px" }}>
          <Cropper
            ref={cropperRef}
            src={imageUrl}
            style={{ height: "100%", width: "100%" }}
            aspectRatio={aspectRatio}
            guides={true}
            onInitialized={handleCropperReady}
            cropBoxResizable={false} // Disable resizing of crop box
            cropBoxMovable={false} // Disable moving the crop box
            zoomable={true} // Allow zoom in/out
            scalable={false} // Disable scaling
            rotatable={false} // Disable rotation
            dragMode="move" // Set drag mode to move image
            viewMode={1} // Restrict the crop box to not exceed the canvas
            minCropBoxWidth={width}
            minCropBoxHeight={height}
          />
        </div>
        <div className="mt-3 text-center">
          <p className="mb-0 text-info fw-bold text-white">
            Drag to move the image • Scroll or pinch to zoom in
          </p>
          <small className="text-muted fw-bold text-white">
            Fixed crop size: {width} x {height}px
          </small>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button className="btn btn-secondary" onClick={onHide}>
          Cancel
        </button>
        <button className="btn btn-primary" onClick={handleCrop}>
          Crop & Save
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default ImageCropper;
